import React, { useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { Card, Pill, SectionHeader } from '../shared';
import { SAGE, CREAM } from '../../theme';

/**
 * The right rail — a LEDGER, not a second chat.
 *
 * Every question asked, tagged with the line that prompted it, plus one
 * general input for anything the page doesn't cover. The distinction matters:
 * modals are where you talk, this is what you've said. It mirrors the
 * interview's right-hand "map" (design §12) — the same idea of showing the
 * reader the shape of what they've done so far.
 *
 * Escalated questions are labelled honestly. A visitor should be able to see
 * that their question is going to a person rather than being quietly dropped.
 */
const ShareLedger = ({ ledger, onAsk, onReopen, isProcessing, mode, senderName = 'the sender' }) => {
  const [input, setInput] = useState('');

  const submit = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || isProcessing) return;
    onAsk(text);
    setInput('');
  };

  return (
    <Card sx={{ p: 1.75, position: 'sticky', top: 24 }}>
      <SectionHeader>Your questions</SectionHeader>

      {ledger.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ mt: 1, fontSize: 12, color: SAGE[400], fontStyle: 'italic', lineHeight: 1.55 }}
        >
          Nothing asked yet. Expand any line to ask about it, or use the box below.
        </Typography>
      ) : (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {ledger.map((entry) => (
            <Box
              key={entry.id}
              sx={{
                p: 1, borderRadius: 1.5, bgcolor: CREAM[100],
                border: `1px solid ${SAGE[100]}`,
              }}
            >
              {entry.anchor && (
                <Typography
                  sx={{
                    fontFamily: "'Lora', Georgia, serif", fontStyle: 'italic',
                    fontSize: 10.5, color: SAGE[400], lineHeight: 1.4, mb: 0.4,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  On “{entry.anchor}”
                </Typography>
              )}
              {!entry.anchor && (
                <Typography sx={{ fontSize: 10.5, color: SAGE[400], mb: 0.4 }}>
                  Not covered on the page
                </Typography>
              )}
              <Typography sx={{ fontSize: 12, color: SAGE[700], lineHeight: 1.5 }}>
                {entry.q}
              </Typography>

              {/* The answer itself. An unanchored question has no modal to read
                  the reply in, so without this the rail would say "answered"
                  and show nothing that was actually answered. */}
              {entry.answer && (
                <Typography
                  sx={{
                    mt: 0.6, pl: 1, fontSize: 11.5, color: SAGE[600], lineHeight: 1.55,
                    borderLeft: `2px solid ${SAGE[200]}`,
                  }}
                >
                  {entry.answer}
                </Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.6, flexWrap: 'wrap' }}>
                <Pill
                  preset={entry.state === 'escalated' ? 'warm' : 'sage'}
                  label={entry.state === 'escalated' ? '\u2197 Going to ' + senderName : 'Answered here'}
                  sx={{ height: 16, fontSize: 9.5 }}
                />
                {entry.threadKey && onReopen && (
                  <Box
                    component="button"
                    onClick={() => onReopen(entry)}
                    sx={{
                      background: 'none', border: 'none', p: 0, cursor: 'pointer',
                      font: 'inherit', fontSize: 10.5, color: SAGE[500],
                      textDecoration: 'underline', textDecorationColor: SAGE[200],
                    }}
                  >
                    reopen
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      <Box component="form" onSubmit={submit} sx={{ mt: 2 }}>
        <SectionHeader>{mode === 'live' ? 'Ask anything else' : 'Ask ' + senderName}</SectionHeader>
        {mode !== 'live' && (
          <Typography sx={{ mt: 0.5, fontSize: 11, color: SAGE[500], lineHeight: 1.5 }}>
            {senderName} is answering these himself — leave your question and he'll
            reply by email.
          </Typography>
        )}
        <TextField
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            mode === 'live'
              ? 'Something the page doesn\u2019t cover?'
              : 'Leave your question…'
          }
          multiline
          minRows={2}
          fullWidth
          size="small"
          disabled={isProcessing}
          sx={{
            mt: 0.75,
            '& .MuiOutlinedInput-root': {
              bgcolor: CREAM[50], fontSize: 12,
              '& fieldset': { borderColor: SAGE[200] },
            },
          }}
        />
        <Button
          type="submit"
          size="small"
          fullWidth
          disabled={!input.trim() || isProcessing}
          sx={{
            mt: 0.75, textTransform: 'none', fontFamily: "'Outfit', sans-serif",
            fontSize: 12, bgcolor: SAGE[500], color: '#fff',
            '&:hover': { bgcolor: SAGE[600] },
            '&.Mui-disabled': { bgcolor: SAGE[100], color: SAGE[300] },
          }}
        >
          {isProcessing ? 'Sending…' : 'Ask'}
        </Button>
      </Box>
    </Card>
  );
};

export default ShareLedger;
